import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download } from 'react-feather';
import './Home.css';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};

const Home = () => {
  useEffect(() => {
    
    // Add floating elements
    const createFloatingElement = () => {
      const element = document.createElement('div');
      element.className = 'floating-element';
      element.style.left = `${Math.random() * 100}%`;
      element.style.top = `${Math.random() * 100}%`;
      element.style.animationDuration = `${Math.random() * 10 + 10}s`;
      element.style.animationDelay = `${Math.random() * 5}s`;
      element.style.opacity = Math.random() * 0.6 + 0.2;
      element.style.pointerEvents = 'none'; // Prevent floating elements from blocking touch events
      
      // Randomly choose between different One Piece themed elements
      const elements = ['⚓', '🏴‍☠️', '☠️', '🔱', '⚔️', '🩻', '🧭', '🛞'];
      element.textContent = elements[Math.floor(Math.random() * elements.length)];
      
      document.querySelector('.floating-elements-container')?.appendChild(element);
      
      // Remove element after animation completes
      setTimeout(() => {
        element.remove();
      }, 30000);
    };
    
    // Create initial floating elements
    for (let i = 0; i < 15; i++) {
      setTimeout(createFloatingElement, i * 1000);
    }
    
    // Continue adding elements periodically
    const interval = setInterval(createFloatingElement, 2000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="home" className="home-section min-vh-100 d-flex align-items-center p-0">
      {/* Floating elements container */}
      <div className="floating-elements-container"></div>
      
      {/* Animated background overlay */}
      <div className="home-overlay"></div>
      
      <div className="container-fluid px-3 px-md-4 px-lg-5">
        <div className="row justify-content-center mx-0">
          <motion.div 
            className="col-12 col-md-10 col-lg-8 px-0"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Logo */}
            <motion.div 
              className="logo-container mb-4 text-center"
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src={process.env.PUBLIC_URL + '/assets/Technix_icon.png'} 
                alt="Technix Icon" 
                className="logo-img"
              />
              <div className="logo-glow"></div>
            </motion.div>
            
            {/* Event Date - Moved up for better visibility */}
            <motion.div 
              className="event-date text-center my-3 d-flex align-items-center justify-content-center"
              variants={itemVariants}
            >
              <div className="date-content mt-4">
                <div className="date-number">4<sup>th</sup> & 5<sup>th</sup></div>
                <div className="date-month">December</div>
                <div className="date-year">2025</div>
              </div>
            </motion.div>
            
            {/* Welcome Title */}
            <motion.div 
              className="welcome-title text-center mb-1"
              variants={itemVariants}
            >
              Welcome Aboard the Grand Line of Technology
            </motion.div>
            
            {/* Quote */}
            <motion.div 
              className="quote-text text-center mt-2 mb-4"
              variants={itemVariants}
            >
              <span className="quote-part">Become the</span>{' '}
              <span className="highlight">Pirate King</span>{' '}
              <span className="quote-part">of Tech!</span>
            </motion.div>
            
            {/* Prize Pool Badge */}
            <motion.div 
              className="prize-badge text-center my-4"
              variants={itemVariants}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ 
                delay: 0.4,
                type: 'spring',
                stiffness: 200,
                damping: 15
              }}
            >
              <div className="prize-badge-inner">
                <div className="prize-amount">₹40,000</div>
                <div className="prize-label">Prize Pool</div>
              </div>
              <div className="prize-badge-glow"></div>
            </motion.div>
            
            {/* CTA Buttons */}
            <motion.div 
              className="cta-buttons d-flex justify-content-center gap-4 flex-wrap"
              variants={itemVariants}
            >
              <a
                href="#events"
                className="btn btn-primary btn-lg px-4 py-3 d-flex align-items-center"
                style={{
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  userSelect: 'none',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  border: 'none',
                  outline: 'none',
                  position: 'relative',
                  zIndex: 10,
                  transition: 'opacity 0.2s ease',
                  WebkitAppearance: 'none',
                  WebkitFontSmoothing: 'antialiased'
                }}
              >
                <span>Explore Events</span>
                <ArrowRight size={20} className="ms-2" />
              </a>
              
              <a
                href="https://drive.google.com/drive/folders/1jQ8P4ofNzuETzU0CTmCE6loA9nLKZAyF?usp=sharing"
                className="btn btn-outline-warning btn-lg px-4 py-3 d-flex align-items-center"
                target="_blank"
                rel="noopener noreferrer"
                download="Technix_Brochure.pdf"
              >
                <span>Download Brochure</span>
                <Download size={20} className="ms-2" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Home;
